import type { PaperAccount, PolyfutureApiClient } from "./api.js";

/**
 * ADR-100. Alpaca sends the admin back to this path with `code` and `state`. It is registered with
 * Alpaca as the redirect address, so it must match the API's `ALPACA_OAUTH_CALLBACK_PATH`.
 */
export const ALPACA_OAUTH_CALLBACK_PATH = "/administration/paper-accounts/alpaca";

type Captured =
  | { readonly kind: "approved"; readonly code: string; readonly state: string }
  | { readonly kind: "declined" };

export type AlpacaOAuthOutcome =
  | { readonly kind: "connected"; readonly account: PaperAccount }
  | { readonly kind: "failed"; readonly message: string };

let captured: Captured | undefined;
let completion: Promise<AlpacaOAuthOutcome> | undefined;

/**
 * Runs once, before routing: takes Alpaca's answer out of the address bar into memory and replaces
 * the address with the administration page, so the one-time code is not left in history. Nothing
 * is written to browser storage.
 */
export function captureAlpacaOAuthCallback(
  location: Pick<Location, "pathname" | "search">,
  history: Pick<History, "replaceState">,
): void {
  if (location.pathname !== ALPACA_OAUTH_CALLBACK_PATH) return;
  const params = new URLSearchParams(location.search);
  const code = params.get("code");
  const state = params.get("state");
  captured =
    code !== null && code !== "" && state !== null && state !== ""
      ? { kind: "approved", code, state }
      : { kind: "declined" };
  history.replaceState({}, "", "/administration");
}

/** Whether the page opened with an answer from Alpaca that has not been sent yet. */
export function hasAlpacaOAuthCallback(): boolean {
  return captured !== undefined || completion !== undefined;
}

/**
 * Sends Alpaca's answer to the server at most once, however many times a screen mounts, and returns
 * what the server confirmed. Undefined when there is no answer.
 */
export function completeAlpacaOAuthCallback(
  client: Pick<PolyfutureApiClient, "completePaperAccountOAuth">,
): Promise<AlpacaOAuthOutcome> | undefined {
  if (completion !== undefined) return completion;
  const answer = captured;
  if (answer === undefined) return undefined;
  captured = undefined;
  completion =
    answer.kind === "declined"
      ? Promise.resolve({
          kind: "failed",
          message: "Alpaca did not approve the connection, so no account was added.",
        })
      : client.completePaperAccountOAuth(answer.code, answer.state).then(
          (account): AlpacaOAuthOutcome => ({ kind: "connected", account }),
          (caught: unknown): AlpacaOAuthOutcome => ({
            kind: "failed",
            message:
              caught instanceof Error && caught.message.trim() !== ""
                ? caught.message
                : "The server did not confirm the connection.",
          }),
        );
  return completion;
}

/** Forgets any answer. Used once a screen has shown the outcome, and by tests. */
export function resetAlpacaOAuthCallback(): void {
  captured = undefined;
  completion = undefined;
}
